/* eslint-disable lingui/no-unlocalized-strings, lingui/text-restrictions -- Approved First Round plan copy. */
import { type ReactNode, useEffect } from "react";

import { planIntentInPath, rememberPlanIntent } from "@/client/components/plan-dialog/plan-intent";
import {
  codeOffer,
  formatPlanPrice,
  useCodeCheck,
  useFirstRoundPlans,
} from "@/client/services/pricing/first-round";

type Props = {
  /** Where sign-in / sign-up goes next (useAuthRedirect). */
  redirect: string;
  mode: "signup" | "signin";
};

/**
 * On sign-up and sign-in, the plan someone picked on firstround.com.au, so they know it wasn't
 * lost: "Starter pass · ~~$69~~ $55.20 with SAVE20. Create your account, then pay." It also keeps
 * the choice (plan-intent.ts) so the plan dialog opens with it however sign-in finishes.
 */
export const PlanIntentSummary = ({ redirect, mode }: Props) => {
  const intent = planIntentInPath(redirect);
  const plans = useFirstRoundPlans();
  const { data: check } = useCodeCheck(intent?.code);

  const plan = intent?.plan;
  const code = intent?.code;
  useEffect(() => {
    if (plan || code) rememberPlanIntent({ plan, code });
  }, [plan, code]);

  if (!intent) return null;
  const card = plans.data?.find((p) => p.key === intent.plan && p.key !== "free");
  const offer = card ? codeOffer(check, card.key) : null;
  const then = mode === "signup" ? "Create your account" : "Sign in";

  let title: ReactNode;
  let next: string;
  if (card && check?.kind === "pass" && offer) {
    title = (
      <>
        <strong>{card.name} pass</strong> · free with {check.code}
      </>
    );
    next = `${then}, then activate it.`;
  } else if (card) {
    const was = offer ? card.price : card.compareAtPrice;
    title = (
      <>
        <strong>{card.name} pass</strong> ·{" "}
        {was !== null && (
          <>
            <s className="text-slate-400">{formatPlanPrice(was, card.currency)}</s>{" "}
          </>
        )}
        {formatPlanPrice(offer ? offer.price : card.price, card.currency)}
        {offer && check ? ` with ${check.code}` : ""}
      </>
    );
    next = `${then}, then pay. One-off, no subscription.`;
  } else if (check) {
    title = (
      <>
        Code <strong>{check.code}</strong> ·{" "}
        {check.kind === "pass" ? `free ${check.planName} pass` : check.label}
      </>
    );
    next = `${then}, and it's ready when you choose a plan.`;
  } else {
    return null;
  }

  return (
    <div
      className="mb-6 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-relaxed text-slate-600"
      role="status"
    >
      <p className="text-slate-900">{title}</p>
      <p className="mt-0.5">{next}</p>
    </div>
  );
};
